import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import QRCode from "qrcode";
import { DateTime } from "luxon";
import {
  Bell,
  CalendarDays,
  Download,
  FileText,
  Laptop,
  PenLine,
  QrCode,
  Shield,
  Trash2,
} from "lucide-react";
import { api, download } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import {
  fmtDate,
  fmtDateTime,
  fmtMinutes,
  monthISO,
  relative,
} from "../../lib/format";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorBox,
  Field,
  Input,
  KeyValue,
  PageHeader,
  Spinner,
  cx,
  useToast, PasswordInput } from "../../components/ui";
import { DayTypeBadge } from "../../components/status";
import { TwoFactorSetup } from "../auth";
import { DevicePush } from '../../components/device-app';
import { SignatureBadges, SignRecordModal } from "../../components/record-signing";
import { MonthSwitcher } from "./History";

export function SecuritySettings() {
  const { me, reload } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [pw, setPw] = useState({ currentPassword: "", newPassword: "" });
  const [disable, setDisable] = useState({ password: "", code: "" });
  const sessions = useQuery({
    queryKey: ["sessions"],
    queryFn: () => api.get<any[]>("/api/v1/auth/sessions"),
  });
  const trusted = useQuery({
    queryKey: ["trusted-devices"],
    queryFn: () => api.get<any[]>("/api/v1/auth/trusted-devices"),
    enabled: Boolean(me?.user.totpEnabled),
  });
  const forget = useMutation({
    mutationFn: (id?: string) =>
      id
        ? api.del(`/api/v1/auth/trusted-devices/${id}`)
        : api.del("/api/v1/auth/trusted-devices"),
    onSuccess: () => (
      toast.success(
        "Dispositivo olvidado: pedirá el código en el próximo acceso",
      ),
      qc.invalidateQueries({ queryKey: ["trusted-devices"] })
    ),
    onError: toast.error,
  });
  const changePw = useMutation({
    mutationFn: () => api.post("/api/v1/auth/password", pw),
    onSuccess: () => (
      toast.success(
        "Contraseña actualizada. Se han cerrado tus otras sesiones.",
      ),
      setPw({ currentPassword: "", newPassword: "" })
    ),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/auth/sessions/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sessions"] }),
  });
  const disable2fa = useMutation({
    mutationFn: () => api.post("/api/v1/auth/2fa/disable", disable),
    onSuccess: () => (
      toast.success("Verificación en dos pasos desactivada"),
      reload()
    ),
  });
  return (
    <div className="space-y-4">
      <Card
        title="Verificación en dos pasos"
        description="Protege tu cuenta con un código temporal además de la contraseña."
      >
        {me?.user.totpEnabled ? (
          <div className="space-y-3">
            <Alert tone="success">Activada.</Alert>
            <div>
              <div className="mb-1 flex items-center justify-between">
                <p className="text-sm font-medium">Dispositivos de confianza</p>
                {(trusted.data?.length ?? 0) > 1 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => forget.mutate(undefined)}
                  >
                    Olvidar todos
                  </Button>
                )}
              </div>
              <p className="mb-2 text-xs text-slate-500">
                No piden el código durante 30 días. Se olvidan solos si cambias
                la contraseña o desactivas la verificación.
              </p>
              {trusted.data?.length ? (
                <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 text-sm">
                  {trusted.data.map((d: any) => (
                    <li
                      key={d.id}
                      className="flex items-center justify-between gap-2 px-3 py-2"
                    >
                      <span className="min-w-0">
                        <span className="block truncate">
                          {d.deviceName || d.userAgent || "Dispositivo"}
                        </span>
                        <span className="text-xs text-slate-500">
                          Último uso {fmtDateTime(d.lastUsedAt)} · caduca{" "}
                          {fmtDateTime(d.expiresAt)}
                        </span>
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => forget.mutate(d.id)}
                      >
                        Olvidar
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-500">
                  Ninguno: se pedirá el código en cada inicio de sesión.
                </p>
              )}
            </div>
            <details className="text-sm">
              <summary className="cursor-pointer text-slate-600">
                Desactivar
              </summary>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <PasswordInput
                  placeholder="Contraseña"
                  value={disable.password}
                  onChange={(e) =>
                    setDisable({ ...disable, password: e.target.value })
                  }
                  aria-label="Contraseña"
                />
                <Input
                  placeholder="Código"
                  value={disable.code}
                  onChange={(e) =>
                    setDisable({ ...disable, code: e.target.value })
                  }
                  aria-label="Código"
                />
                <Button
                  variant="danger"
                  onClick={() => disable2fa.mutate()}
                  loading={disable2fa.isPending}
                >
                  Desactivar
                </Button>
              </div>
              <ErrorBox error={disable2fa.error} />
            </details>
          </div>
        ) : (
          <TwoFactorSetup onDone={() => reload()} />
        )}
      </Card>
      <Card title="Cambiar contraseña">
        <form
          className="grid gap-3 sm:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            changePw.mutate();
          }}
        >
          <PasswordInput
            autoComplete="current-password"
            placeholder="Contraseña actual"
            value={pw.currentPassword}
            onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })}
            aria-label="Contraseña actual"
          />
          <PasswordInput
            autoComplete="new-password"
            placeholder="Nueva contraseña"
            value={pw.newPassword}
            onChange={(e) => setPw({ ...pw, newPassword: e.target.value })}
            aria-label="Nueva contraseña"
          />
          <Button type="submit" loading={changePw.isPending}>
            Actualizar
          </Button>
        </form>
        <ErrorBox error={changePw.error} />
      </Card>
      <Card title="Sesiones y dispositivos" padded={false}>
        {sessions.isLoading ? (
          <Spinner />
        ) : (
          <ul className="divide-y divide-slate-100">
            {sessions.data?.map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between gap-3 px-5 py-3 text-sm"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <Laptop className="h-5 w-5 shrink-0 text-slate-400" />
                  <div className="min-w-0">
                    <p className="truncate font-medium">
                      {s.deviceName?.slice(0, 60) ??
                        s.userAgent?.slice(0, 60) ??
                        "Dispositivo"}
                    </p>
                    <p className="text-xs text-slate-500">
                      {s.ip} · activa {relative(s.lastUsedAt)}{" "}
                      {s.current && <Badge color="green">Esta sesión</Badge>}
                    </p>
                  </div>
                </div>
                {!s.current && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => revoke.mutate(s.id)}
                    aria-label="Cerrar sesión"
                    icon={<Trash2 className="h-4 w-4" />}
                  >
                    Cerrar
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

export function NotificationPrefs() {
  const toast = useToast();
  const { data, refetch } = useQuery({
    queryKey: ["notif-prefs"],
    queryFn: () => api.get<any[]>("/api/v1/me/notification-preferences"),
  });
  const [prefs, setPrefs] = useState<
    Record<string, { inApp: boolean; email: boolean; push: boolean }>
  >({});
  useEffect(() => {
    if (data)
      setPrefs(
        Object.fromEntries(
          data.map((p) => [
            p.type,
            { inApp: p.inApp, email: p.email, push: p.push },
          ]),
        ),
      );
  }, [data]);
  const save = useMutation({
    mutationFn: () => api.put("/api/v1/me/notification-preferences", { prefs }),
    onSuccess: () => (toast.success("Preferencias guardadas"), refetch()),
  });
  if (!data) return <Spinner />;
  return (
    <Card
      title="Preferencias de notificación"
      actions={
        <Button
          size="sm"
          onClick={() => save.mutate()}
          loading={save.isPending}
        >
          Guardar
        </Button>
      }
      padded={false}
    >
      <table className="table-base">
        <thead>
          <tr>
            <th>Aviso</th>
            <th className="text-center">En la app</th>
            <th className="text-center">Email</th>
            <th className="text-center">Push</th>
          </tr>
        </thead>
        <tbody>
          {data.map((p) => (
            <tr key={p.type}>
              <td>{p.label}</td>
              {(["inApp", "email", "push"] as const).map((ch) => (
                <td key={ch} className="text-center">
                  <input
                    type="checkbox"
                    aria-label={`${p.label} · ${ch}`}
                    checked={prefs[p.type]?.[ch] ?? false}
                    onChange={(e) =>
                      setPrefs({
                        ...prefs,
                        [p.type]: { ...prefs[p.type]!, [ch]: e.target.checked },
                      })
                    }
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function KioskQr() {
  const [qr, setQr] = useState<string | null>(null);
  const [expires, setExpires] = useState(0);
  const gen = async () => {
    const r = await api.get<{ token: string; expiresInSeconds: number }>(
      "/api/v1/me/qr-token",
    );
    setQr(await QRCode.toDataURL(r.token, { margin: 1, width: 240 }));
    setExpires(Date.now() + r.expiresInSeconds * 1000);
  };
  useEffect(() => {
    if (!qr) return;
    const t = setInterval(() => {
      if (Date.now() > expires - 5000) gen();
    }, 3000);
    return () => clearInterval(t);
  }, [qr, expires]);
  return (
    <Card
      title="Código QR para el kiosco"
      description="Muéstralo en el kiosco de tu centro para identificarte. Se renueva automáticamente."
    >
      {qr ? (
        <div className="flex flex-col items-center gap-2">
          <img
            src={qr}
            alt="Código QR personal de identificación"
            className="h-56 w-56"
          />
          <p className="text-xs text-slate-500">Válido 90 segundos</p>
        </div>
      ) : (
        <Button
          variant="secondary"
          icon={<QrCode className="h-4 w-4" />}
          onClick={gen}
        >
          Mostrar código QR
        </Button>
      )}
    </Card>
  );
}

export function WorkerProfile() {
  const { me } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="space-y-4">
      <PageHeader title="Perfil" />
      <Card>
        <KeyValue
          items={[
            ["Nombre", me?.user.fullName],
            ["Email", me?.user.email],
            ["Empresa", me?.company?.name],
            ["Código de trabajador", me?.company?.employee?.code],
          ]}
        />
      </Card>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Button
          variant="secondary"
          icon={<CalendarDays className="h-4 w-4" />}
          onClick={() => navigate("/app/calendar")}
        >
          Mi calendario
        </Button>
        <Button
          variant="secondary"
          icon={<FileText className="h-4 w-4" />}
          onClick={() => navigate("/app/documents")}
        >
          Mis documentos
        </Button>
        <Button
          variant="secondary"
          icon={<Bell className="h-4 w-4" />}
          onClick={() => navigate("/app/notifications")}
        >
          Notificaciones
        </Button>
        <Button
          variant="secondary"
          icon={<Shield className="h-4 w-4" />}
          onClick={() => navigate("/app/privacy")}
        >
          Privacidad
        </Button>
      </div>
      {me?.company?.plan.features.includes("kiosk") && <KioskQr />}
      <SecuritySettings />
      <DevicePush />
      <NotificationPrefs />
    </div>
  );
}

export function WorkerDocuments() {
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ["me", "documents"],
    queryFn: () => api.get<any[]>("/api/v1/me/documents"),
  });
  const [month, setMonth] = useState(monthISO());
  const [signing, setSigning] = useState<{ id: string; title: string } | null>(null);
  return (
    <div className="space-y-4">
      <SignRecordModal doc={signing} onClose={() => setSigning(null)} />
      <PageHeader
        title="Mis documentos"
        description="Tu registro horario de cada mes. Fírmalo para dar tu conformidad (o indica qué no es correcto)."
      />
      <Card title="Descargar mi registro de cualquier mes">
        <div className="flex flex-wrap items-center gap-3">
          <MonthSwitcher month={month} onChange={setMonth} />
          {(["PDF", "XLSX", "CSV"] as const).map((f) => (
            <Button
              key={f}
              size="sm"
              variant="secondary"
              icon={<Download className="h-4 w-4" />}
              onClick={() =>
                download(`/api/v1/me/reports/${month}?format=${f}`).catch(
                  toast.error,
                )
              }
            >
              {f}
            </Button>
          ))}
        </div>
      </Card>
      <Card title="Publicados por la empresa" padded={false}>
        {isLoading ? (
          <Spinner />
        ) : !data?.length ? (
          <EmptyState title="Aún no hay documentos publicados" />
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.map((d) => (
              <li
                key={d.id}
                className="flex items-center justify-between gap-3 px-5 py-3 text-sm"
              >
                <div>
                  <p className="font-medium">{d.title}</p>
                  <p className="text-xs text-slate-500">
                    {fmtDateTime(d.createdAt)} · SHA-256 {d.sha256.slice(0, 12)}
                    …
                  </p>
                  {d.signable && <SignatureBadges d={d} />}
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  {d.signable && !d.mySignature && (
                    <Button size="sm" icon={<PenLine className="h-4 w-4" />} onClick={() => setSigning(d)}>
                      Firmar
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="secondary"
                    icon={<Download className="h-4 w-4" />}
                    onClick={() =>
                      download(`/api/v1/documents/${d.id}/${d.mySignature || d.companySignature ? "signed" : "download"}`).catch(toast.error)
                    }
                  >
                    {d.mySignature || d.companySignature ? "Descargar firmado" : "Descargar"}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

export function NotificationsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["notifications", "all"],
    queryFn: () =>
      api.get<{ unread: number; rows: any[] }>(
        "/api/v1/me/notifications?limit=100",
      ),
  });
  const read = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/me/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const readAll = useMutation({
    mutationFn: () => api.post("/api/v1/me/notifications/read-all"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  return (
    <div>
      <PageHeader
        title="Notificaciones"
        actions={
          data?.unread ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => readAll.mutate()}
            >
              Marcar todas como leídas
            </Button>
          ) : null
        }
      />
      {isLoading ? (
        <Spinner />
      ) : !data?.rows.length ? (
        <Card>
          <EmptyState
            icon={<Bell className="h-5 w-5" />}
            title="No tienes notificaciones"
          />
        </Card>
      ) : (
        <ul className="space-y-2">
          {data.rows.map((n) => (
            <li key={n.id}>
              <button
                onClick={() => {
                  if (!n.readAt) read.mutate(n.id);
                  if (n.data?.path) navigate(n.data.path);
                }}
                className={cx(
                  "card flex w-full items-start gap-3 p-4 text-left hover:border-brand-300",
                  !n.readAt && "border-brand-200 bg-brand-50/40",
                )}
              >
                {!n.readAt && (
                  <span
                    className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-600"
                    aria-label="Sin leer"
                  />
                )}
                <div className="min-w-0">
                  <p className="font-medium">{n.title}</p>
                  <p className="text-sm text-slate-600">{n.body}</p>
                  <p className="mt-1 text-xs text-slate-400">
                    {relative(n.createdAt)}
                  </p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function WorkerCalendar() {
  const [month, setMonth] = useState(monthISO());
  const { data, isLoading } = useQuery({
    queryKey: ["me", "calendar", month],
    queryFn: () => api.get<any[]>(`/api/v1/me/calendar?month=${month}`),
  });
  const first = DateTime.fromISO(`${month}-01`);
  const pad = first.weekday - 1;
  return (
    <div>
      <PageHeader
        title="Mi calendario"
        actions={<MonthSwitcher month={month} onChange={setMonth} />}
      />
      {isLoading ? (
        <Spinner />
      ) : (
        <div className="card overflow-hidden">
          <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50 text-center text-xs font-semibold text-slate-500">
            {["L", "M", "X", "J", "V", "S", "D"].map((d) => (
              <div key={d} className="py-2">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {Array.from({ length: pad }).map((_, i) => (
              <div
                key={`p${i}`}
                className="min-h-20 border-b border-r border-slate-100 bg-slate-50/50"
              />
            ))}
            {data?.map((d) => (
              <div
                key={d.date}
                className="min-h-20 border-b border-r border-slate-100 p-1.5 text-xs"
              >
                <p className="font-semibold text-slate-700">
                  {Number(d.date.slice(8))}
                </p>
                {d.segments.length ? (
                  <p className="text-slate-600">
                    {d.segments
                      .map((s: any) => `${s.start}–${s.end}`)
                      .join(" ")}
                  </p>
                ) : (
                  <DayTypeBadge dayType={d.dayType} />
                )}
                {d.label && (
                  <p className="truncate text-violet-700">{d.label}</p>
                )}
                {d.record?.effectiveMinutes ? (
                  <p className="text-emerald-700">
                    {fmtMinutes(d.record.effectiveMinutes)}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function PrivacyPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["legal", "docs"],
    queryFn: () => api.get<any[]>("/api/v1/legal/documents"),
  });
  const accept = useMutation({
    mutationFn: (id: string) =>
      api.post(`/api/v1/legal/documents/${id}/accept`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["legal"] }),
  });
  return (
    <div>
      <PageHeader
        title="Privacidad y documentos legales"
        description="Información sobre el tratamiento de tus datos y el uso de la geolocalización."
      />
      {isLoading ? (
        <Spinner />
      ) : !data?.length ? (
        <Card>
          <EmptyState title="Tu empresa aún no ha publicado documentos" />
        </Card>
      ) : (
        <div className="space-y-3">
          {data.map((d) => (
            <Card
              key={d.id}
              title={`${d.title} · v${d.version}`}
              actions={
                d.acceptedAt ? (
                  <Badge color="green">
                    Aceptado {fmtDate(d.acceptedAt.slice(0, 10))}
                  </Badge>
                ) : d.requiresAcceptance ? (
                  <Button size="sm" onClick={() => accept.mutate(d.id)}>
                    He leído y acepto
                  </Button>
                ) : null
              }
            >
              <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700">
                {d.content}
              </pre>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
